import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import usePageMeta from "@/hooks/usePageMeta";
import { FadeIn, SectionHeading } from "@/components/Reveal";
import CTASection from "@/components/CTASection";
import { posts } from "@/data/posts";

const formatDate = (d) =>
  new Date(d + "T12:00:00").toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

const Blog = () => {
  usePageMeta(
    "Blog — Relationship Insights | Holistic Healing Psychotherapy, Inc.",
    "Practical, compassionate writing on couples therapy, communication, emotional distance and rebuilding trust — from a California LCSW with nearly 20 years of experience."
  );
  const [featured, ...rest] = posts;

  return (
    <div data-testid="blog-page">
      <section className="bg-cream-50 pt-14 pb-16 lg:pt-24 lg:pb-20">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <FadeIn>
            <p className="eyebrow mb-6" data-testid="blog-eyebrow">The Journal</p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.1] text-ink max-w-3xl" data-testid="blog-headline">
              Honest writing for <em className="text-forest-700">hard seasons</em>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-sage leading-relaxed max-w-2xl" data-testid="blog-subcopy">
              Short, practical reflections on relationships, conflict, trust and change — written the way I'd explain
              them across from you in a session.
            </p>
          </FadeIn>

          <FadeIn delay={0.15}>
            <Link
              to={`/blog/${featured.slug}`}
              data-testid={`blog-card-${featured.slug}`}
              className="group mt-14 block rounded-2xl border border-forest-800/10 bg-forest-900 p-8 sm:p-12 transition-all duration-500 hover:border-gold-500/40 hover:shadow-[0_24px_60px_rgba(18,55,42,0.25)]"
            >
              <p className="eyebrow mb-4">Featured · {formatDate(featured.date)} · {featured.readTime}</p>
              <h2 className="font-serif text-2xl sm:text-4xl text-cream-50 leading-snug max-w-3xl group-hover:text-gold-300 transition-colors duration-300">
                {featured.title}
              </h2>
              <p className="mt-4 text-sm sm:text-base text-cream-50/65 leading-relaxed max-w-2xl">{featured.excerpt}</p>
              <span className="inline-flex items-center gap-2 mt-7 text-xs font-semibold uppercase tracking-[0.2em] text-gold-400 group-hover:gap-3 transition-all duration-300">
                Read the article <ArrowRight size={14} />
              </span>
            </Link>
          </FadeIn>

          <div className="mt-8 grid md:grid-cols-2 gap-6">
            {rest.map((post, i) => (
              <FadeIn key={post.slug} delay={0.1 + i * 0.08}>
                <Link
                  to={`/blog/${post.slug}`}
                  data-testid={`blog-card-${post.slug}`}
                  className="group block h-full rounded-2xl border border-forest-800/10 bg-cream-100 p-8 transition-all duration-500 hover:border-gold-500/50 hover:-translate-y-1 hover:shadow-[0_20px_50px_rgba(18,55,42,0.12)]"
                >
                  <div className="flex items-start justify-between gap-4">
                    <p className="text-xs uppercase tracking-[0.2em] text-sage">
                      {formatDate(post.date)} · {post.readTime}
                      {post.lang === "es" && (
                        <span className="ml-2 rounded-full border border-gold-500/50 text-gold-700 px-2 py-0.5">Español</span>
                      )}
                    </p>
                    <ArrowUpRight size={18} className="text-gold-500 shrink-0 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                  </div>
                  <h2 className="font-serif text-xl sm:text-2xl text-forest-800 leading-snug mt-4">{post.title}</h2>
                  <p className="mt-3 text-sm text-sage leading-relaxed">{post.excerpt}</p>
                </Link>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-cream-100 py-16 lg:py-20">
        <div className="max-w-4xl mx-auto px-5 sm:px-8">
          <SectionHeading eyebrow="New Here?" title="Start where it hurts most." />
          <FadeIn delay={0.1}>
            <p className="mt-4 text-base text-sage leading-relaxed max-w-2xl" data-testid="blog-services-note">
              These articles reflect the same approach I bring to the therapy room. If something here sounds like
              your relationship, the{" "}
              <Link to="/couples-therapy" className="text-gold-700 underline underline-offset-4 hover:text-gold-600" data-testid="blog-couples-link">couples therapy</Link>{" "}
              and{" "}
              <Link to="/infidelity-trust" className="text-gold-700 underline underline-offset-4 hover:text-gold-600" data-testid="blog-infidelity-link">infidelity & trust</Link>{" "}
              pages go deeper.
            </p>
          </FadeIn>
        </div>
      </section>

      <CTASection
        title="Reading about it is a start. Talking about it changes things."
        copy="Schedule a free 20-minute consultation to see whether we're a good fit."
      />
    </div>
  );
};

export default Blog;
